/**
 * MovementSummary Component
 * Post-workout analysis of user movement through the 5x5 grid
 * Displays accuracy metrics, heatmap, and personalized suggestions
 */
import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Target, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  ArrowRight,
  Activity,
  Compass,
  Layers
} from 'lucide-react';
import {
  calculatePatternAccuracy,
  generateHeatmap,
  analyzeMovementPatterns,
} from '@/utils/gridMapping';

const LEVEL_LABELS = {
  1: 'Legs',
  2: 'Hips',
  3: 'Core',
  4: 'Shoulders',
  5: 'Head',
};

const CHAMBER_LABELS = {
  1: 'Far Left',
  2: 'Left',
  3: 'Center',
  4: 'Right',
  5: 'Far Right',
};

const MovementSummary = ({
  movementHistory = [],
  rideType = 'chamber-ride',
  expectedChamber = 3,
  expectedLevel = 3,
  exerciseName = 'Exercise',
  assessmentData = null,
  onClose,
  onRetry,
}) => {
  // Calculate pattern accuracy
  const accuracyResult = useMemo(() => {
    return calculatePatternAccuracy(movementHistory, rideType, expectedChamber, expectedLevel);
  }, [movementHistory, rideType, expectedChamber, expectedLevel]);

  // Generate heatmap data
  const heatmapData = useMemo(() => {
    return generateHeatmap(movementHistory);
  }, [movementHistory]);

  // Analyze movement patterns
  const patternAnalysis = useMemo(() => {
    return analyzeMovementPatterns(movementHistory);
  }, [movementHistory]);

  // Calculate assessment-based challenges
  const assessmentChallenges = useMemo(() => {
    if (!assessmentData) return [];
    
    const challenges = [];
    
    // Check if user avoided their "opposite" regions based on assessment
    if (assessmentData.legPreference) {
      const oppositeLevel = assessmentData.legPreference === 'left' ? 
        { chamber: 4, label: 'right side (chambers 4-5)' } : 
        { chamber: 2, label: 'left side (chambers 1-2)' };
      
      const oppositeVisits = movementHistory.filter(p => 
        assessmentData.legPreference === 'left' ? 
          p.chamber >= 4 : p.chamber <= 2
      ).length;
      
      if (oppositeVisits < movementHistory.length * 0.2) {
        challenges.push({
          type: 'warning',
          message: `You stayed mostly on your dominant side. Try incorporating more ${oppositeLevel.label} movements.`,
        });
      }
    }
    
    // Check level distribution based on assessment comfort zones
    if (assessmentData.comfortLevel) {
      const comfortLevel = parseInt(assessmentData.comfortLevel);
      const oppositeRegion = comfortLevel > 3 ? 'lower levels (1-2)' : 'higher levels (4-5)';
      const oppositeVisits = movementHistory.filter(p => 
        comfortLevel > 3 ? p.level <= 2 : p.level >= 4
      ).length;
      
      if (oppositeVisits < movementHistory.length * 0.15) {
        challenges.push({
          type: 'info',
          message: `Challenge yourself to explore ${oppositeRegion} more often.`,
        });
      }
    }
    
    return challenges;
  }, [assessmentData, movementHistory]);

  // Get accuracy color and icon
  const getAccuracyStyle = (accuracy) => {
    if (accuracy >= 80) return { color: 'text-shadow-pine', bg: 'bg-shadow-pine/20', icon: CheckCircle };
    if (accuracy >= 50) return { color: 'text-burnt-terra', bg: 'bg-burnt-terra/20', icon: AlertTriangle };
    return { color: 'text-red-600', bg: 'bg-red-100', icon: XCircle };
  };

  const accuracyStyle = getAccuracyStyle(accuracyResult.accuracy);
  const AccuracyIcon = accuracyStyle.icon;

  // Get pattern display name
  const getPatternName = (type) => {
    const names = {
      'chamber-ride': 'Chamber Ride',
      'level-ride': 'Level Ride',
      'chamber-lock': 'Chamber Lock',
      'level-lock': 'Level Lock',
      'x-factor': 'X Factor',
    };
    return names[type] || type;
  };

  // Render heatmap grid
  const renderHeatmap = () => {
    return (
      <div className="grid grid-cols-5 gap-1 p-2 bg-ivory-mist/80 rounded-lg">
        {heatmapData.map((row, rowIndex) => (
          row.map((value, colIndex) => {
            const level = 5 - rowIndex;
            const chamber = colIndex + 1;
            const intensity = Math.round(value * 100);
            
            return (
              <div
                key={`${rowIndex}-${colIndex}`}
                className="aspect-square rounded flex items-center justify-center text-xs font-bold relative group cursor-default"
                style={{
                  backgroundColor: `rgba(199, 91, 62, ${value * 0.8 + 0.1})`,
                  color: value > 0.5 ? 'white' : '#1a1a2e',
                }}
                title={`Chamber ${chamber}, Level ${level}: ${intensity}% of time`}
              >
                {intensity > 0 && `${intensity}%`}
                
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-black text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                  C{chamber} L{level}: {CHAMBER_LABELS[chamber]}, {LEVEL_LABELS[level]}
                </div>
              </div>
            );
          })
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="border-b">
          <CardTitle className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-burnt-terra" />
            Movement Analysis: {exerciseName}
          </CardTitle>
        </CardHeader>
        
        <CardContent className="space-y-6 p-6">
          {/* Accuracy Score */}
          <div className={`${accuracyStyle.bg} rounded-lg p-6 text-center`}>
            <div className="flex items-center justify-center gap-3 mb-2">
              <AccuracyIcon className={`w-10 h-10 ${accuracyStyle.color}`} />
              <span className={`text-5xl font-bold ${accuracyStyle.color}`}>
                {accuracyResult.accuracy}%
              </span>
            </div>
            <p className="text-lg font-semibold text-shadow-pine">
              Pattern Accuracy: {getPatternName(rideType)}
            </p>
            <p className="text-shadow-pine/70 mt-1">
              {accuracyResult.details.message}
            </p>
          </div>

          {/* Pattern Details */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-shadow-pine/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Compass className="w-5 h-5 text-shadow-pine" />
                <h3 className="font-semibold text-shadow-pine">Chambers Visited</h3>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(chamber => {
                  const visited = patternAnalysis.chamberCounts?.[chamber] > 0;
                  return (
                    <div
                      key={chamber}
                      className={`flex-1 h-8 rounded flex items-center justify-center text-xs font-bold ${
                        visited ? 'bg-shadow-pine text-white' : 'bg-shadow-pine/10 text-shadow-pine/50'
                      }`}
                    >
                      {chamber}
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="bg-burnt-terra/10 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Layers className="w-5 h-5 text-burnt-terra" />
                <h3 className="font-semibold text-burnt-terra">Levels Visited</h3>
              </div>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map(level => {
                  const visited = patternAnalysis.levelCounts?.[level] > 0;
                  return (
                    <div
                      key={level}
                      className={`flex-1 h-8 rounded flex items-center justify-center text-xs font-bold ${
                        visited ? 'bg-burnt-terra text-white' : 'bg-shadow-pine/10 text-shadow-pine/50'
                      }`}
                    >
                      {level}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Movement Heatmap */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Target className="w-5 h-5 text-burnt-terra" />
              <h3 className="font-semibold">Position Heatmap</h3>
            </div>
            {renderHeatmap()}
            <div className="flex justify-between text-xs text-shadow-pine/60 mt-2 px-2">
              <span>← Left</span>
              <span>Right →</span>
            </div>
            <div className="flex justify-between text-xs text-shadow-pine/60 mt-1">
              <span>Level 5 (Head) ↑</span>
              <span>Level 1 (Legs) ↓</span>
            </div>
          </div>

          {/* Movement Bias Analysis */}
          {(patternAnalysis.dominantSide !== 'balanced' || patternAnalysis.verticalBias !== 'balanced') && (
            <div className="bg-ivory-mist rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-shadow-pine" />
                <h3 className="font-semibold">Movement Tendencies</h3>
              </div>
              <ul className="space-y-2 text-sm">
                {patternAnalysis.dominantSide !== 'balanced' && (
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-shadow-pine/60" />
                    <span>
                      <strong>Horizontal:</strong> Favors {patternAnalysis.dominantSide} side
                    </span>
                  </li>
                )}
                {patternAnalysis.verticalBias !== 'balanced' && (
                  <li className="flex items-center gap-2">
                    <ArrowRight className="w-4 h-4 text-shadow-pine/60" />
                    <span>
                      <strong>Vertical:</strong> Tends toward {patternAnalysis.verticalBias} positions
                    </span>
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Assessment-Based Challenges */}
          {assessmentChallenges.length > 0 && (
            <div className="bg-burnt-terra/10 border border-burnt-terra/30 rounded-lg p-4">
              <h3 className="font-semibold text-obsidian-night mb-2">Assessment-Based Challenges</h3>
              <ul className="space-y-2">
                {assessmentChallenges.map((challenge, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-burnt-terra">
                    <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{challenge.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Suggestions */}
          {patternAnalysis.suggestions.length > 0 && (
            <div className="bg-ivory-mist border border-shadow-pine/30 rounded-lg p-4">
              <h3 className="font-semibold text-obsidian-night mb-2">Suggestions for Improvement</h3>
              <ul className="space-y-2">
                {patternAnalysis.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-shadow-pine">
                    <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="bg-ivory-mist rounded-lg p-3">
              <p className="text-2xl font-bold text-shadow-pine">
                {movementHistory.length}
              </p>
              <p className="text-xs text-shadow-pine/60">Data Points</p>
            </div>
            <div className="bg-ivory-mist rounded-lg p-3">
              <p className="text-2xl font-bold text-burnt-terra">
                {new Set(movementHistory.map(p => `${p.chamber}-${p.level}`)).size}
              </p>
              <p className="text-xs text-shadow-pine/60">Unique Positions</p>
            </div>
            <div className="bg-ivory-mist rounded-lg p-3">
              <p className="text-2xl font-bold text-shadow-pine">
                {patternAnalysis.mostVisitedChamber ? 
                  `C${patternAnalysis.mostVisitedChamber}` : '-'}
              </p>
              <p className="text-xs text-shadow-pine/60">Most Visited Chamber</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              onClick={onRetry}
              variant="outline"
              className="flex-1"
            >
              Try Again
            </Button>
            <Button
              onClick={onClose}
              className="flex-1 bg-burnt-terra hover:bg-burnt-terra/90"
            >
              Continue
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MovementSummary;
